import { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { movies, articles } from "@/mocks/data";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movieId = parseInt(id || "0", 10);
  const movie = movies.find((m) => m.id === movieId);
  const article = articles.find((a) => a.id === movieId);

  useEffect(() => {
    if (movie) {
      document.title = `${movie.title} - 大师兄影视`;
    }
  }, [movie]);

  const relatedMovies = useMemo(() => {
    if (!article) return [];
    return movies.filter((m) => article.relatedIds.includes(m.id));
  }, [article]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">影片未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您查找的影片不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium cursor-pointer no-underline hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="max-w-[1000px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: movie.category === "movie" ? "电影" : movie.category === "drama" ? "电视剧" : movie.category === "variety" ? "综艺" : movie.category === "anime" ? "动漫" : "纪录片", path: `/category?type=${movie.category}` },
                { label: movie.title },
              ]}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
            <div className="w-full md:w-[280px] shrink-0">
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                <img
                  src={movie.poster}
                  alt={movie.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">
                {movie.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">{movie.englishTitle}</p>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="px-2.5 py-1 text-xs rounded-full bg-primary-100 text-primary-700 font-medium">
                  {movie.rating} 分
                </span>
                <span className="px-2.5 py-1 text-xs rounded-full bg-background-200 text-foreground-600">
                  {movie.year}
                </span>
                <span className="px-2.5 py-1 text-xs rounded-full bg-background-200 text-foreground-600">
                  {movie.duration}
                </span>
                <span className="px-2.5 py-1 text-xs rounded-full bg-background-200 text-foreground-600">
                  {movie.region}
                </span>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-xs font-medium text-foreground-400 whitespace-nowrap w-14 shrink-0">类型：</span>
                  <span className="text-sm text-foreground-800">{movie.genre}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-xs font-medium text-foreground-400 whitespace-nowrap w-14 shrink-0">导演：</span>
                  <span className="text-sm text-foreground-800">{movie.director}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-xs font-medium text-foreground-400 whitespace-nowrap w-14 shrink-0">主演：</span>
                  <span className="text-sm text-foreground-800">{movie.cast.join(" / ")}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-xs font-medium text-foreground-400 whitespace-nowrap w-14 shrink-0">状态：</span>
                  <span className="text-sm text-primary-600 font-medium">{movie.updateInfo}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">
                  <i className="ri-play-circle-fill text-lg"></i>
                  立即播放
                </button>
                <button className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-background-200 text-foreground-700 text-sm font-medium cursor-pointer hover:bg-background-300 transition-colors whitespace-nowrap">
                  <i className="ri-heart-line"></i>
                  收藏
                </button>
                <button className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-background-200 text-foreground-700 text-sm font-medium cursor-pointer hover:bg-background-300 transition-colors whitespace-nowrap">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>

          <section className="mb-10">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">剧情简介</h2>
            <p className="text-sm text-foreground-700 leading-relaxed">{movie.description}</p>
          </section>

          {article && (
            <section className="mb-10">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">深度解读</h2>
              <div className="rounded-xl bg-background-100 p-5 md:p-6 mb-4">
                <img
                  src={article.poster}
                  alt={article.title}
                  className="w-full h-48 md:h-64 object-cover rounded-lg mb-4"
                />
                <h3 className="text-base font-semibold text-foreground-950 mb-2">{article.title}</h3>
                <div className="flex items-center gap-4 mb-3">
                  <span className="text-xs text-foreground-500">{article.date}</span>
                  <span className="text-xs text-foreground-500">作者：{article.author}</span>
                  <span className="text-xs text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>
                    {article.views.toLocaleString()} 阅读
                  </span>
                </div>
                <div
                  className="text-sm text-foreground-700 leading-relaxed space-y-3 article-content"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>
            </section>
          )}

          {relatedMovies.length > 0 && (
            <section>
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                {relatedMovies.map((m) => (
                  <MovieCard
                    key={m.id}
                    id={m.id}
                    title={m.title}
                    genre={m.genre}
                    rating={m.rating}
                    poster={m.poster}
                    updateInfo={m.updateInfo}
                    isNew={m.isNew}
                    isHot={m.isHot}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article?.title || movie.title,
            "description": article?.summary || movie.description,
            "datePublished": article?.date || `${movie.year}-01-01`,
            "author": {
              "@type": "Organization",
              "name": article?.author || "大师兄影视编辑部",
            },
            "publisher": {
              "@type": "Organization",
              "name": "大师兄影视",
            },
            "image": movie.poster,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://www.dashixiong.tv/article/${movie.id}`,
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "首页",
                  "item": "https://www.dashixiong.tv/",
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": movie.category === "movie" ? "电影" : "电视剧",
                  "item": `https://www.dashixiong.tv/category?type=${movie.category}`,
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": movie.title,
                },
              ],
            },
          }),
        }}
      />
    </div>
  );
}